import { apiRequest, PlayerApiError } from '@/api/client';
import { AGENT_ENROLL_CODE } from '@/launch-params';

/**
 * The player's part in enrolling the Pi's root agent (plan 03, D3).
 *
 * The agent cannot pair itself: it has no screen to show a code on. It opens an
 * enrollment request and the kiosk launcher appends the code to this page's URL.
 * Once an admin has paired *this* browser, its device token is the proof that
 * the agent lives on a Pi the organization owns, so the player presents the code
 * with that token. The agent's own poll then turns the claim into its credential.
 *
 * Fire-and-forget by design: nothing on screen depends on it, and a failure
 * leaves only "agent not enrolled" in the admin UI, which the agent repairs by
 * rotating its code and restarting the browser.
 */

const DONE_KEY = 'l42.agentClaimDone';
const FIRST_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 60_000;
const GIVE_UP_AFTER_MS = 30 * 60_000;

export interface AgentClaimDeps {
  code: string | null;
  post: (code: string) => Promise<void>;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
}

export type AgentClaimOutcome = 'no_code' | 'already_done' | 'claimed' | 'expired' | 'gave_up';

const defaultDeps = (): AgentClaimDeps => ({
  code: AGENT_ENROLL_CODE,
  post: (code) => apiRequest<void>('/devices/agent/claim', { method: 'POST', body: { code } }),
  storage: window.sessionStorage,
  sleep: (ms) => new Promise((resolve) => window.setTimeout(resolve, ms)),
  now: () => Date.now(),
});

let inFlight: Promise<AgentClaimOutcome> | null = null;

/**
 * Claims the launch URL's enrollment code once per page. Safe to call from
 * every place a paired player can start — a second call while one is running
 * joins it, and a call after success is a no-op for the rest of the session.
 */
export function claimAgentEnrollment(
  deps: Partial<AgentClaimDeps> = {},
): Promise<AgentClaimOutcome> {
  if (inFlight) return inFlight;
  const d = { ...defaultDeps(), ...deps };
  inFlight = run(d).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(d: AgentClaimDeps): Promise<AgentClaimOutcome> {
  if (!d.code) return 'no_code';
  const doneKey = `${DONE_KEY}:${d.code}`;
  if (safeGet(d.storage, doneKey) === '1') return 'already_done';
  stripCodeFromUrl();

  const started = d.now();
  let backoff = FIRST_BACKOFF_MS;
  for (;;) {
    try {
      await d.post(d.code);
      safeSet(d.storage, doneKey, '1');
      return 'claimed';
    } catch (err) {
      if (err instanceof PlayerApiError) {
        // 409: already claimed — by this device on an earlier page, which the
        // server answers 204 for, or by another, which no retry will change.
        if (err.status === 409) {
          safeSet(d.storage, doneKey, '1');
          return 'claimed';
        }
        // 404: the code expired or was used. The agent rotates it and restarts
        // the browser with a fresh one; retrying this one is pointless.
        if (err.status === 404) return 'expired';
        // 401/403: not paired (yet). The pairing view calls again once it is.
        if (err.status === 401 || err.status === 403) return 'gave_up';
      }
      if (d.now() - started + backoff > GIVE_UP_AFTER_MS) return 'gave_up';
      await d.sleep(backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    }
  }
}

/** Cosmetic: the value was captured at module load and is not read again. */
function stripCodeFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('agentEnrollCode')) return;
    url.searchParams.delete('agentEnrollCode');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // A sandboxed or file:// origin may refuse; nothing depends on it.
  }
}

function safeGet(storage: AgentClaimDeps['storage'], key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: AgentClaimDeps['storage'], key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Without storage the worst case is one extra, idempotent claim per load.
  }
}
